import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { MotifIconButton } from '../../../button/icon-button/icon-button';
import { ReactComponent as NavigationIcArrowDropUp24px } from '../../../../assets/icons/navigation/ic_arrow_drop_up_24px.svg';
import { ReactComponent as NavigationIcArrowDropDown24px } from '../../../../assets/icons/navigation/ic_arrow_drop_down_24px.svg';
import { ReactComponent as ContentIcFilterList24px } from '../../../../assets/icons/content/ic_filter_list_24px.svg';

const SORT_ASC = 'SORT_ASC';
const SORT_DESC = 'SORT_DESC';
const SORT_NOSORT = 'SORT_NOSORT';

const sortData = {
  [SORT_ASC]: { sort: 'desc', ariaLabel: 'sort descending' },
  [SORT_DESC]: { sort: '', ariaLabel: 'remove sorting' },
  [SORT_NOSORT]: { sort: 'asc', ariaLabel: 'sort ascending' }
};

const defaultIcons = {
  ascending: <NavigationIcArrowDropUp24px />,
  descending: <NavigationIcArrowDropDown24px />,
  filter: <ContentIcFilterList24px />
};

/**
 * @typedef Props
 * @prop {{ascending: React.ReactNode, descending: React.ReactNode, filter: React.ReactNode}} [icons]
 * @prop {{}} [column]
 * @prop {boolean} [enableSorting]
 * @prop {boolean} [showFilter]
 * @prop {string} [displayName]
 * @prop {(x: string, event: React.SyntheticEvent) => void} [setSort]
 * @prop {(e: React.SyntheticEvent) => void} [showColumnMenu]
 * @prop {boolean} [dataColumn]
 * @prop {React.ReactNode} [headerEndComponent]
 */
/**
 * @param {Props} props
 */
const MotifTableHeaderRenderer = ({
  icons: userIcons = {},
  column = null,
  enableSorting = false,
  showFilter = false,
  displayName = '',
  setSort = null,
  showColumnMenu = null,
  dataColumn = true,
  headerEndComponent = null
}) => {
  const icons = {
    ...defaultIcons,
    ...userIcons
  };
  const [stateSort, setStateSort] = useState(SORT_NOSORT);

  useEffect(() => {
    column.addEventListener('sortChanged', onSortChanged);
    onSortChanged();

    return () => {
      column.removeEventListener('sortChanged', onSortChanged);
    };
  }, [column]);

  const onSortChanged = () => {
    let curSort = SORT_NOSORT;
    if (column.isSortAscending()) {
      curSort = SORT_ASC;
    } else if (column.isSortDescending()) {
      curSort = SORT_DESC;
    }
    setStateSort(curSort);
  };

  const onSortRequested = event => {
    if (enableSorting) {
      setSort(sortData[stateSort].sort, event.shiftKey);
    }
  };

  const handleShowFilter = evt => showColumnMenu(evt.target);

  const renderCustomHeader = () => (
    <div
      className="motif-table-header-renderer"
      title={enableSorting ? sortData[stateSort].ariaLabel : ''}
      aria-label={displayName}
      data-motif-table-header-renderer
    >
      <div
        className="motif-table-header-title"
        onClick={onSortRequested}
        onTouchEnd={onSortRequested}
      >
        {displayName}
      </div>
      {enableSorting && (
        <div
          role="button"
          className="motif-table-header-icons"
          onClick={onSortRequested}
          onTouchEnd={onSortRequested}
        >
          {stateSort === SORT_NOSORT && (
            <div className="motif-table-header-no-sort">
              {icons.ascending}
              {icons.descending}
            </div>
          )}
          {stateSort === SORT_ASC && icons.ascending}
          {stateSort === SORT_DESC && icons.descending}
        </div>
      )}
      {showFilter && (
        <MotifIconButton
          type="button"
          onClick={handleShowFilter}
          aria-label="Column filter"
          className="motif-table-header-filtertrigger"
        >
          {icons.filter}
        </MotifIconButton>
      )}
    </div>
  );

  const renderEndComponent = () => (
    <div className="motif-table-header-renderer" data-motif-table-header-renderer>
      <div className="motif-table-header-end-icons">{headerEndComponent}</div>
    </div>
  );

  return dataColumn ? renderCustomHeader() : renderEndComponent();
};

MotifTableHeaderRenderer.propTypes = {
  icons: PropTypes.shape({
    ascending: PropTypes.node,
    descending: PropTypes.node,
    filter: PropTypes.node
  }),
  // Provided by ag-grid, always available
  column: PropTypes.objectOf(PropTypes.any),

  // Flag provided by ag-grid to activate sorting functionality, TRUE by ag-grid
  enableSorting: PropTypes.bool,

  // Flag, show Filter menu button on TRUE
  showFilter: PropTypes.bool,

  // Header column name to be displayed. If null ag-grid will use the headerName property in the column definition.
  displayName: PropTypes.string,

  // Provided by ag-grid, always available
  setSort: PropTypes.func,

  // Provided by ag-grid, always available
  showColumnMenu: PropTypes.func,

  // Flag, if FALSE renders headerEndComponent
  dataColumn: PropTypes.bool,

  // Component to be rendered, useful only if dataColumn=FALSE
  headerEndComponent: PropTypes.oneOfType([PropTypes.element, PropTypes.elementType])
};

export { MotifTableHeaderRenderer };
